import { useParams, Link, Navigate } from 'react-router-dom';
import { getCityBySlug } from '../data/citiesData';
import { formatILS, formatPercent } from '../utils/format';
import RiskBadge from '../components/ui/RiskBadge';
import ScoreBar from '../components/ui/ScoreBar';
import { neighborhoodsByCity } from '../data/neighborhoodsData';
import imgKeys from '../assets/images/interior-kitchen.jpg';

export default function CityDetail() {
  const { slug } = useParams();
  const city = getCityBySlug(slug);
  if (!city) return <Navigate to="/villes" replace />;

  const airbnbYield = ((city.airbnb.estimatedAnnualRevenue / city.rentalYields[0].averagePrice) * 100).toFixed(1);
  const neighborhoods = neighborhoodsByCity[city.id] || [];

  // SEO — update page title & meta per city
  document.title = `Investir à ${city.name} en 2025 — Prix, rendements, opportunités | Deal Israel`;
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute('content', `${city.name} : prix au m² ${city.averagePricePerSqm.toLocaleString()} ₪, rendement locatif ${city.rentalYields[0].grossYield}–${city.rentalYields[2].grossYield}%, ${city.shortComment} Analyse complète 2025.`);

  const blockStyle = {
    background: 'var(--color-bg-white)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    border: '1px solid var(--color-border)',
    marginBottom: '1.5rem',
  };

  return (
    <div className="page-content">
      <div className="container">
        {/* Breadcrumb */}
        <div style={{ marginBottom: '1.5rem', fontSize: '0.82rem', color: 'var(--color-text-muted)', display: 'flex', gap: '0.5rem', alignItems: 'center', letterSpacing: '0.02em' }}>
          <Link to="/villes" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Retour aux villes</Link>
          <span style={{ color: 'var(--color-border)' }}>/</span>
          <span>{city.name}</span>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <span className="badge badge-blue">{city.region}</span>
              <RiskBadge level={city.riskLevel} />
              <span className="badge badge-neutral">Attractivité {city.attractivenessScore}/10</span>
            </div>
            <h1 style={{ marginBottom: '0.5rem' }}>{city.name}</h1>
            <p style={{ fontStyle: 'italic', fontSize: '1.05rem', color: 'var(--color-text-muted)' }}>{city.shortComment}</p>
          </div>
          <Link to="/simulateurs" className="btn btn-accent">Simuler le rendement</Link>
        </div>

        {/* Description */}
        <div style={blockStyle}>
          <p style={{ margin: 0, lineHeight: 1.85, fontSize: '1rem', color: 'var(--color-text)' }}>{city.description}</p>
        </div>

        {/* Prix + Profil */}
        <div className="grid-2" style={{ marginBottom: '1.5rem' }}>
          <div style={blockStyle}>
            <h3 className="card-title">Prix au m²</h3>
            <div style={{ textAlign: 'center', margin: '1.25rem 0' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>{formatILS(city.averagePricePerSqm)}</span>
              <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}> / m² (moyenne)</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', background: 'var(--color-surface)', borderRadius: 'var(--radius-md)', padding: '1rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Entrée de gamme</div>
                <strong style={{ fontFamily: 'var(--font-display)' }}>{formatILS(city.priceRange.min)}/m²</strong>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Haut de gamme</div>
                <strong style={{ fontFamily: 'var(--font-display)' }}>{formatILS(city.priceRange.max)}/m²</strong>
              </div>
            </div>
          </div>
          <div style={blockStyle}>
            <h3 className="card-title">Profil investisseur</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Score d'attractivité</div>
                <ScoreBar score={city.attractivenessScore} />
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Niveau de risque</div>
                <RiskBadge level={city.riskLevel} />
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Profil idéal</div>
                <strong style={{ color: 'var(--color-primary)', fontSize: '0.92rem' }}>{city.recommendedProfile}</strong>
              </div>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '1.25rem 0' }} />
            <p style={{ fontSize: '0.875rem', margin: 0, lineHeight: 1.7 }}>{city.riskComment}</p>
          </div>
        </div>

        {/* Rendements */}
        <div style={blockStyle}>
          <h3 className="card-title">Rendements locatifs classiques</h3>
          <div className="table-wrapper" style={{ marginTop: '1rem' }}>
            <table>
              <thead>
                <tr>
                  <th>Type de bien</th>
                  <th>Prix d'achat</th>
                  <th>Loyer mensuel</th>
                  <th>Revenu annuel</th>
                  <th>Rendement brut</th>
                </tr>
              </thead>
              <tbody>
                {city.rentalYields.map((row, i) => (
                  <tr key={i}>
                    <td><strong>{row.unitType}</strong></td>
                    <td>{formatILS(row.averagePrice)}</td>
                    <td>{formatILS(row.averageMonthlyRent)}/mois</td>
                    <td>{formatILS(row.averageMonthlyRent * 12)}/an</td>
                    <td>
                      <span style={{ fontWeight: 700, fontSize: '1rem', color: row.grossYield >= 4 ? 'var(--color-success)' : 'var(--color-primary)' }}>
                        {formatPercent(row.grossYield)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Airbnb */}
        <div style={{ ...blockStyle, background: 'var(--color-bg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
            <h3 className="card-title" style={{ margin: 0 }}>Potentiel location courte durée</h3>
            <span className="badge badge-warning">Estimation indicative</span>
          </div>
          <div className="grid-4" style={{ marginBottom: '1.25rem' }}>
            {[
              { val: formatILS(city.airbnb.averageDailyRate), lbl: 'Tarif journalier' },
              { val: `${city.airbnb.averageOccupancyRate} %`, lbl: "Taux d'occupation" },
              { val: formatILS(city.airbnb.estimatedAnnualRevenue, true), lbl: 'Revenu annuel brut' },
              { val: `${airbnbYield} %`, lbl: 'Rendement (studio)' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '1.25rem 1rem', background: 'var(--color-bg-white)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>{s.val}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{s.lbl}</div>
              </div>
            ))}
          </div>
          <div className="insight-box" style={{ margin: 0 }}>
            Ces estimations sont annualisées. Le rendement réel dépend du quartier, de la qualité du bien et de la réglementation locale applicable.
          </div>
        </div>

        {/* Quartiers */}
        {neighborhoods.length > 0 && (
          <div style={blockStyle}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 className="card-title" style={{ margin: 0 }}>Prix par quartier</h3>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Quartier</th><th>Prix moy./m²</th><th>Loyer 2P/mois</th><th>Rendement brut</th><th>Profil</th></tr>
                </thead>
                <tbody>
                  {neighborhoods.map((n, i) => (
                    <tr key={i}>
                      <td><strong style={{ fontFamily: 'var(--font-display)', color: 'var(--color-primary)' }}>{n.name}</strong></td>
                      <td><strong>{formatILS(n.avgPricePerSqm)}</strong></td>
                      <td>{n.avgMonthlyRent2P ? formatILS(n.avgMonthlyRent2P) : '–'}</td>
                      <td><span style={{ fontWeight: 700, color: n.avgYield >= 4 ? 'var(--color-success)' : 'var(--color-primary)' }}>{formatPercent(n.avgYield)}</span></td>
                      <td><span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>{n.profile}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={{ background: '#0d1f3c', backgroundImage: `linear-gradient(rgba(13,31,60,0.80), rgba(13,31,60,0.80)), url(${imgKeys})`, backgroundSize: 'cover', backgroundPosition: 'center', borderRadius: 'var(--radius-lg)', padding: '2.25rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ color: '#fafaf8', margin: 0, marginBottom: '0.5rem' }}>Vous vous intéressez à {city.name} ?</h3>
            <p style={{ color: 'rgba(250,250,248,0.7)', margin: 0, fontSize: '0.95rem' }}>Nos partenaires francophones connaissent ce marché en détail.</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/accompagnement" className="btn btn-accent">Être accompagné</Link>
            <Link to="/simulateurs" className="btn btn-outline-white">Simuler mon rendement</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
